/**
 * Utility functions for safely handling Supabase query results
 * These functions help prevent TypeScript errors when relations are missing
 * and provide fallbacks for malformed data
 */

/**
 * Safely maps an array, handling null/undefined cases
 */
export function mapArraySafe<T>(rows: any[] | null | undefined, map: (r: any) => T): T[] {
  if (!Array.isArray(rows)) return [];
  return rows.map(map);
}

/**
 * Detects and filters out SelectQueryError objects from Supabase
 * Supabase returns `{ hint: '...' }` for SelectQueryError – detect by presence of `hint`
 */
export function notSelectQueryError<T extends object>(obj: any | undefined): T | undefined {
  if (!obj || typeof obj !== 'object') return undefined;
  // if it has "code" or "hint", treat as error-ish
  if ('hint' in obj || 'code' in obj) return undefined;
  return obj as T;
}

/**
 * Safely extracts a nested relation from a Supabase row
 */
export function safeRelation<T extends object>(row: any, key: string): T | undefined {
  const relation = row?.[key];
  return notSelectQueryError<T>(relation);
}

/**
 * Safely extracts an array relation from a Supabase row
 */
export function safeArrayRelation<T extends object>(row: any, key: string): T[] {
  const relation = row?.[key];
  if (!Array.isArray(relation)) return [];
  return relation.filter(item => notSelectQueryError<T>(item));
}

/**
 * Normalizes a message row to ensure it matches the Message interface
 */
export function normalizeMessage(row: any): any {
  if (!row || typeof row !== 'object') return null;
  
  // Handle SelectQueryError
  if ('hint' in row || 'code' in row) return null;
  
  return {
    id: row.id,
    sender_id: row.sender_id,
    receiver_id: row.receiver_id,
    content: row.content,
    timestamp: row.timestamp,
    read: row.read,
    sender: safeRelation(row, 'sender'),
    reactions: safeArrayRelation(row, 'reactions'),
  };
}

/**
 * Normalizes a group message row to ensure it matches the GroupMessage interface
 */
export function normalizeGroupMessage(row: any): any {
  if (!row || typeof row !== 'object') return null;
  
  // Handle SelectQueryError
  if ('hint' in row || 'code' in row) return null;
  
  return {
    id: row.id,
    group_id: row.group_id,
    sender_id: row.sender_id,
    content: row.content,
    timestamp: row.timestamp,
    sender: safeRelation(row, 'sender'),
    reactions: safeArrayRelation(row, 'reactions'),
  };
}

/**
 * Normalizes a group row to ensure it matches the Group interface
 */
export function normalizeGroup(row: any): any {
  if (!row || typeof row !== 'object') return null;
  
  // Handle SelectQueryError
  if ('hint' in row || 'code' in row) return null;
  
  return {
    id: row.id,
    name: row.name,
    description: row.description,
    photo_url: row.photo_url,
    created_by: row.created_by,
    created_at: row.created_at,
    members: safeArrayRelation(row, 'members'),
  };
}

/**
 * Normalizes a group member row to ensure it matches the GroupMember interface
 */
export function normalizeGroupMember(row: any): any {
  if (!row || typeof row !== 'object') return null;
  
  // Handle SelectQueryError
  if ('hint' in row || 'code' in row) return null;
  
  return {
    id: row.id,
    group_id: row.group_id,
    user_id: row.user_id,
    role: row.role,
    joined_at: row.joined_at,
    user: safeRelation(row, 'user'),
  };
}

/**
 * Normalizes a user profile row to ensure it matches the UserProfile interface
 */
export function normalizeUserProfile(row: any): any {
  if (!row || typeof row !== 'object') return null;
  
  // Handle SelectQueryError
  if ('hint' in row || 'code' in row) return null;
  
  return {
    id: row.id,
    user_id: row.user_id,
    email: row.email,
    full_name: row.full_name,
    preferred_name: row.preferred_name,
    display_name: row.display_name,
    username: row.username,
    avatar_url: row.avatar_url,
    bio: row.bio,
    location: row.location,
    website: row.website,
    social_links: row.social_links,
    skills: row.skills,
    experience: row.experience,
    education: row.education,
    portfolio: row.portfolio,
    resume_url: row.resume_url,
    role: row.role,
    account_type: row.account_type,
    public_profile: row.public_profile,
    // Must be carried through: useUserProfile derives isProfileComplete from it,
    // and dropping it here left that permanently false — which silently hid the
    // notification bell (NotificationCenter returns null unless the profile is
    // complete) for every user.
    onboarding_completed: row.onboarding_completed,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

/**
 * Normalizes a user record row to ensure it matches the UserRecord interface
 */
export function normalizeUserRecord(row: any): any {
  if (!row || typeof row !== 'object') return null;
  
  // Handle SelectQueryError
  if ('hint' in row || 'code' in row) return null;
  
  return {
    id: row.id,
    email: row.email,
    role: row.role,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

/**
 * Normalizes a network user row to ensure it matches the NetworkUser interface
 */
export function normalizeNetworkUser(row: any): any {
  if (!row || typeof row !== 'object') return null;
  
  // Handle SelectQueryError
  if ('hint' in row || 'code' in row) return null;
  
  return {
    id: row.id,
    full_name: row.full_name,
    preferred_name: row.preferred_name,
    username: row.username,
    avatar_url: row.avatar_url,
    bio: row.bio,
    location: row.location,
    skills: row.skills,
    experience: row.experience,
    education: row.education,
    account_type: row.account_type,
    public_profile: row.public_profile,
    created_at: row.created_at,
  };
}

/**
 * Normalizes a public profile row to ensure it matches the PublicProfile interface
 */
export function normalizePublicProfile(row: any): any {
  if (!row || typeof row !== 'object') return null;
  
  // Handle SelectQueryError
  if ('hint' in row || 'code' in row) return null;
  
  return {
    users: row.users,
    id: row.id,
    email: row.email,
    full_name: row.full_name,
    preferred_name: row.preferred_name,
    display_name: row.display_name,
    username: row.username,
    avatar_url: row.avatar_url,
    bio: row.bio,
    location: row.location,
    website: row.website,
    social_links: row.social_links,
    skills: row.skills,
    experience: row.experience,
    education: row.education,
    portfolio: row.portfolio,
    resume_url: row.resume_url,
    role: row.role,
    account_type: row.account_type,
    public_profile: row.public_profile,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

/**
 * Normalizes a media file row to ensure it matches the MediaFile interface
 */
export function normalizeMediaFile(row: any): any {
  if (!row || typeof row !== 'object') return null;
  
  // Handle SelectQueryError
  if ('hint' in row || 'code' in row) return null;
  
  return {
    id: row.id,
    user_id: row.user_id,
    file_type: row.file_type,
    file_url: row.file_url,
    file_name: row.file_name,
    file_size: row.file_size,
    visibility: row.visibility,
    created_at: row.created_at,
  };
}

/**
 * Normalizes a user preferences row to ensure it matches the UserPreferences interface
 */
export function normalizeUserPreferences(row: any): any {
  if (!row || typeof row !== 'object') return null;
  
  // Handle SelectQueryError
  if ('hint' in row || 'code' in row) return null;
  
  return {
    id: row.id,
    user_id: row.user_id,
    font_style: row.font_style,
    font_color: row.font_color,
    theme: row.theme,
    notifications: row.notifications,
    privacy: row.privacy,
    accessibility: row.accessibility,
    language: row.language,
    timezone: row.timezone,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

/**
 * Normalizes a reel row to ensure it matches the Reel interface
 */
export function normalizeReel(row: any): any {
  if (!row || typeof row !== 'object') return null;
  
  // Handle SelectQueryError
  if ('hint' in row || 'code' in row) return null;
  
  return {
    id: row.id,
    title: row.title,
    category: row.category,
    description: row.description,
    media_url: row.media_url,
    thumbnail_url: row.thumbnail_url,
    duration: row.duration,
    created_at: row.created_at,
  };
}

/**
 * Normalizes a post interaction row to ensure it matches the PostInteraction interface
 */
export function normalizePostInteraction(row: any): any {
  if (!row || typeof row !== 'object') return null;
  
  // Handle SelectQueryError
  if ('hint' in row || 'code' in row) return null;
  
  return {
    id: row.id,
    post_id: row.post_id,
    user_id: row.user_id,
    type: row.type,
    created_at: row.created_at,
  };
}

/**
 * Safely extracts a string value from a potentially malformed object
 */
export function safeString(obj: any, key: string, fallback: string = ''): string {
  if (!obj || typeof obj !== 'object') return fallback;
  const value = obj[key];
  return typeof value === 'string' ? value : fallback;
}

/**
 * Safely extracts a number value from a potentially malformed object
 */
export function safeNumber(obj: any, key: string, fallback: number = 0): number {
  if (!obj || typeof obj !== 'object') return fallback;
  const value = obj[key];
  return typeof value === 'number' ? value : fallback;
}

/**
 * Safely extracts a boolean value from a potentially malformed object
 */
export function safeBoolean(obj: any, key: string, fallback: boolean = false): boolean {
  if (!obj || typeof obj !== 'object') return fallback;
  const value = obj[key];
  return typeof value === 'boolean' ? value : fallback;
}

/**
 * Safely extracts an array value from a potentially malformed object
 */
export function safeArray<T>(obj: any, key: string, fallback: T[] = []): T[] {
  if (!obj || typeof obj !== 'object') return fallback;
  const value = obj[key];
  return Array.isArray(value) ? value : fallback;
}
